import { createAction, createReducer } from "@reduxjs/toolkit";
import { Hoofdcode } from "@/models/hoofdcode";
import { Werkcat } from "@/models/werkcat";
import { Subwerkcat } from "@/models/subwerkcat";
import LocalStorageHelper from "@/localstorage";

// Sort configuration type
export interface SortConfig {
    key: keyof Hoofdcode | 'vermeldingen' | 'bestekken';
    direction: 'asc' | 'desc';
}

// Filter state interface
export interface HoofdcodeFilterState {
    beschrijving: string;
    werk_categorieen: Werkcat[];
    subwerk_categorieen: Subwerkcat[];
    hoofdcodes: Hoofdcode[];
    jaarversie: string | null;
    // NIEUW: "release versie" (Bestek.versie) — cascadeert onder jaarversie.
    release: string | null;
    activePage: number;
    sortConfig: SortConfig | null;
    filterVisible: boolean;
}

// Helper functions voor localStorage
const getInitialFilterVisibility = (): boolean => {
    try {
        const stored = LocalStorageHelper.getItem('hoofdcodeFilterVisible');
        if (stored === null) return true;
        return stored !== 'false';
    } catch (error) {
        console.warn('Error reading hoofdcode filter visibility:', error);
        return true;
    }
};

const saveFilterVisibility = (visible: boolean): void => {
    try {
        LocalStorageHelper.setItem('hoofdcodeFilterVisible', visible.toString());
    } catch (error) {
        console.warn('Error saving hoofdcode filter visibility:', error);
    }
};

// Initial state
const initialState: HoofdcodeFilterState = {
    beschrijving: "",
    werk_categorieen: [],
    subwerk_categorieen: [],
    hoofdcodes: [],
    jaarversie: null,
    release: null,
    activePage: 1,
    sortConfig: null,
    filterVisible: getInitialFilterVisibility()
};

// Actions
const setBeschrijvingAction = createAction<string>('hoofdcode-filter/set-beschrijving');
const setWerkCategorieenAction = createAction<Werkcat[]>('hoofdcode-filter/set-werk-categorieen');
const setSubwerkCategorieenAction = createAction<Subwerkcat[]>('hoofdcode-filter/set-subwerk-categorieen');
const setHoofdcodesAction = createAction<Hoofdcode[]>('hoofdcode-filter/set-hoofdcodes');
const setJaarversieAction = createAction<string | null>('hoofdcode-filter/set-jaarversie');
const setReleaseAction = createAction<string | null>('hoofdcode-filter/set-release');
const setActivePageAction = createAction<number>('hoofdcode-filter/set-active-page');
const setSortConfigAction = createAction<SortConfig | null>('hoofdcode-filter/set-sort-config');
const toggleFilterVisibilityAction = createAction('hoofdcode-filter/toggle-filter-visibility');
const resetAction = createAction('hoofdcode-filter/reset');

// Reducer
export const hoofdcodeFilterReducer = createReducer(initialState, (builder) => {
    builder
        .addCase(setBeschrijvingAction, (state, action) => {
            state.beschrijving = action.payload;
            state.activePage = 1; // Reset page on filter change
        })
        .addCase(setWerkCategorieenAction, (state, action) => {
            state.werk_categorieen = action.payload;
            state.activePage = 1;
        })
        .addCase(setSubwerkCategorieenAction, (state, action) => {
            state.subwerk_categorieen = action.payload;
            state.activePage = 1;
        })
        .addCase(setHoofdcodesAction, (state, action) => {
            state.hoofdcodes = action.payload;
            state.activePage = 1;
        })
        .addCase(setJaarversieAction, (state, action) => {
            state.jaarversie = action.payload;
            // Release cascadeert onder jaarversie; bij wisselen kan de eerder gekozen
            // release niet meer geldig zijn.
            state.release = null;
            state.activePage = 1;
        })
        .addCase(setReleaseAction, (state, action) => {
            state.release = action.payload;
            state.activePage = 1;
        })
        .addCase(setActivePageAction, (state, action) => {
            state.activePage = action.payload;
        })
        .addCase(setSortConfigAction, (state, action) => {
            state.sortConfig = action.payload;
            state.activePage = 1;
        })
        .addCase(toggleFilterVisibilityAction, (state) => {
            state.filterVisible = !state.filterVisible;
            saveFilterVisibility(state.filterVisible);
        })
        .addCase(resetAction, (state) => {
            state.beschrijving = "";
            state.werk_categorieen = [];
            state.subwerk_categorieen = [];
            state.hoofdcodes = [];
            state.jaarversie = null;
            state.release = null;
            state.activePage = 1;
            state.sortConfig = null;
            // filterVisible blijft behouden bij reset
        });
});

// Export actions
export const hoofdcodeFilterActions = {
    set_beschrijving: setBeschrijvingAction,
    set_werk_categorieen: setWerkCategorieenAction,
    set_subwerk_categorieen: setSubwerkCategorieenAction,
    set_hoofdcodes: setHoofdcodesAction,
    set_jaarversie: setJaarversieAction,
    set_release: setReleaseAction,
    set_active_page: setActivePageAction,
    set_sort_config: setSortConfigAction,
    toggle_filter_visibility: toggleFilterVisibilityAction,
    reset: resetAction
};